import { Injectable } from '@angular/core';
import { CallCenter } from '@app/core/interfaces/c5/acd/call-center';
import { CallCenterAgent } from '@app/core/interfaces/c5/acd/call-center-agent';
import { Queue } from '@app/core/interfaces/c5/acd/queue';
import { RouteConfig } from '@app/core/interfaces/c5/acd/route-config';
import { Skill } from '@app/core/interfaces/c5/acd/skill';
import { Announcement } from '@app/core/interfaces/c5/announcement';
import { EnterpriseOptionType } from '@app/core/interfaces/enterprise-option-type';
import { CallCenterAgentRepository } from '@app/core/repositories/call-center-agent.repository';
import { CallCenterAnnouncementRepository } from '@app/core/repositories/call-center-announcement.repository';
import { CallCenterQueueAnnouncementRepository } from '@app/core/repositories/call-center-queue-announcement.repository';
import { CallCenterQueueRouteConfigRepository } from '@app/core/repositories/call-center-queue-route-config.repository';
import { CallCenterSkillRepository } from '@app/core/repositories/call-center-skill.repository';
import { CallCenterRepository } from '@app/core/repositories/call-center.repository';
import { AcdCallCenterUtilsService } from '@app/shared/acd-cc/utils/acd-call-center-utils-service';
import { mapAnnouncements } from '@app/shared/acd-cc/utils/route-config/map-announcements';
import { C5VersionCheckerService } from '@app/shared/services/c5-version-checker.service';
import { CachedHelperService } from '@app/shared/services/cached-helper.service';
import { ContextHelperService } from '@app/shared/services/context/context-helper.service';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { EnterpriseAuthorizationService } from '@app/shared/services/enterprise-authorization.service';
import { Cached, cachedGroup } from '@app/utils/cached';
import { wrapError } from '@app/utils/rxjs';
import { CallCenterAndQueueWithUser } from '@business/call-center/models/call-center-and-queue-with-user';
import { CallCenterContext } from '@business/call-center/models/call-center-context';
import { CallCenterQueueContext } from '@business/call-center/models/call-center-queue-context';
import { CallCenterSettingsViewModel } from '@business/call-center/models/call-center-settings-view-model';
import { forkJoin, Observable, of } from 'rxjs';
import { delayWhen, first, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CallCenterService {
  private _agents: Cached<CallCenterAgent[]> | null = null;

  get agents(): Cached<CallCenterAgent[]> {
    this._agents ??= cachedGroup(
      [this.callCenterContext],
      ([{ callCenter }]) => {
        return this.callCenterAgentRepository.list(callCenter.id);
      },
      {
        id: `call-center:agents`,
      }
    );

    return this._agents;
  }

  private _announcements: Cached<Announcement[]> | null = null;

  get announcements(): Cached<Announcement[]> {
    this._announcements ??= cachedGroup(
      [this.callCenterContext],
      ([{ callCenter }]) => {
        return this.callCenterAnnouncementRepository.list(callCenter.id);
      },
      {
        id: `call-center:announcements`,
      }
    );

    return this._announcements;
  }

  private _callCenterContext: Cached<CallCenterContext> | null = null;

  get callCenterContext(): Cached<CallCenterContext> {
    this._callCenterContext ??= cachedGroup(
      [this.enterpriseContextService.callCenter],
      ([callCenter]) => {
        return this.pcrEnabled$.pipe(
          map((pcrEnabled): CallCenterContext => {
            return {
              callCenter,
              pcrEnabled,
            };
          })
        );
      },
      {
        id: 'call-center:context',
      }
    );

    return this._callCenterContext;
  }

  get callCenter$(): Observable<CallCenter> {
    return this.callCenterContext.data$.pipe(map(({ callCenter }) => callCenter));
  }

  get pcrEnabled$(): Observable<boolean> {
    const hasCallRecService$ = this.enterpriseAuthorizationService
      .hasEnterpriseOption$(EnterpriseOptionType.CallRecService)
      .pipe(first());

    const callRecPermanentFeatures$ = this.enterpriseAuthorizationService
      .getEnterpriseOption$(EnterpriseOptionType.CallRecPermanentFeatures)
      .pipe(first());

    return forkJoin([hasCallRecService$, callRecPermanentFeatures$]).pipe(
      map(([hasCallRecService, callRecPermanentFeatures]) => {
        return hasCallRecService && callRecPermanentFeatures?.available > 0;
      })
    );
  }

  private _queue: Cached<CallCenterAndQueueWithUser> | null = null;

  get queue(): Cached<CallCenterAndQueueWithUser> {
    this._queue ??= this.cachedHelperService.routeAwareCached({
      dataProducer: ([, callCenterId, queueId]) =>
        this.callCenterRepository.getQueue(Number(callCenterId), Number(queueId)),
      id: ([, callCenterId, queueId]) => `call-center:${callCenterId}:queue:${queueId}`,
      routeParameters: ['enterpriseId', 'callCenterId', 'queueId'],
    });

    return this._queue;
  }

  private _queueAnnouncements: Cached<Announcement[]> | null = null;

  get queueAnnouncements(): Cached<Announcement[]> {
    this._queueAnnouncements ??= cachedGroup(
      [this.queue],
      ([{ callCenter, queueWithUser }]) => {
        return this.queueAnnouncementRepository.list(callCenter.id, queueWithUser.queue.id);
      },
      {
        id: `call-center:queue:announcements`,
      }
    );

    return this._queueAnnouncements;
  }

  private _queueContext: Cached<CallCenterQueueContext> | null = null;

  get queueContext(): Cached<CallCenterQueueContext> {
    this._queueContext ??= cachedGroup(
      [this.queue, this.queues, this.enterpriseContextService.callCenter, this.queueAnnouncements],
      ([{ callCenter, queueWithUser }, queues, cc, announcements]) => {
        callCenter.callback = cc.callback;
        const pcrState$ = this.acdCallCenterUtilsService.getPcrState(queueWithUser.user);
        const routingGroups$ = this.contextHelperService.reloadAndGet$(
          this.enterpriseContextService.routingGroupsForSite(queueWithUser.queue.siteId)
        );

        return forkJoin([pcrState$, routingGroups$]).pipe(
          map(([pcrState, routingGroups]): CallCenterQueueContext => {
            return {
              callCenter,
              announcements: mapAnnouncements(announcements, 'cc'),
              pcrAvailable: pcrState.available,
              pcrEnabled: pcrState.enabled,
              queue: this.acdCallCenterUtilsService.enhanceQueue(queueWithUser.queue, queueWithUser.user),
              queues: queues.map((queue) => ({
                key: queue.name,
                label: queue.name,
                value: queue.id,
              })),
              routingGroups,
              user: queueWithUser.user,
            };
          })
        );
      },
      {
        id: 'call-center:queue-context',
      }
    );

    return this._queueContext;
  }

  private _queues: Cached<Queue[]> | null = null;

  get queues(): Cached<Queue[]> {
    this._queues ??= cachedGroup(
      [this.callCenterContext],
      ([{ callCenter }]) => this.callCenterRepository.listQueues(callCenter.id),
      {
        id: `call-center:queues`,
      }
    );

    return this._queues;
  }

  private _queueRouteConfigs: Cached<RouteConfig[]> | null = null;

  get queueRouteConfigs(): Cached<RouteConfig[]> {
    this._queueRouteConfigs ??= this.cachedHelperService.routeAwareCached({
      dataProducer: ([callCenterId, queueId]) => {
        return this.callCenterQueueRouteConfigRepository.list(Number(callCenterId), Number(queueId));
      },
      id: ([callCenterId, queueId]) => `call-center:${callCenterId}:queue:${queueId}:route-configs`,
      routeParameters: ['callCenterId', 'queueId'],
    });

    return this._queueRouteConfigs;
  }

  private _settings: Cached<CallCenterSettingsViewModel> | null = null;

  get settings(): Cached<CallCenterSettingsViewModel> {
    this._settings ??= cachedGroup(
      [this.callCenterContext, this.announcements, this.c5VersionCheckerService.c5Version],
      ([callCenterContext, announcements, c5Version]): Observable<CallCenterSettingsViewModel> => {
        const c5IsAtLeastR7_8 = this.c5VersionCheckerService.atLeast(c5Version, '7.8');

        return of({
          announcements,
          callCenter: callCenterContext.callCenter,
          isAtLeastC5R7_8: c5IsAtLeastR7_8,
        });
      },
      {
        id: 'call-center:settings',
      }
    );

    return this._settings;
  }

  private _skills: Cached<Skill[]> | null = null;

  get skills(): Cached<Skill[]> {
    this._skills ??= cachedGroup(
      [this.callCenterContext],
      ([{ callCenter }]) => this.callCenterSkillRepository.list(callCenter.id),
      {
        id: `call-center:skills`,
      }
    );

    return this._skills;
  }

  constructor(
    private acdCallCenterUtilsService: AcdCallCenterUtilsService,
    private c5VersionCheckerService: C5VersionCheckerService,
    private cachedHelperService: CachedHelperService,
    private callCenterAgentRepository: CallCenterAgentRepository,
    private callCenterAnnouncementRepository: CallCenterAnnouncementRepository,
    private callCenterQueueRouteConfigRepository: CallCenterQueueRouteConfigRepository,
    private callCenterRepository: CallCenterRepository,
    private callCenterSkillRepository: CallCenterSkillRepository,
    private contextHelperService: ContextHelperService,
    private enterpriseAuthorizationService: EnterpriseAuthorizationService,
    private enterpriseContextService: EnterpriseContextService,
    private queueAnnouncementRepository: CallCenterQueueAnnouncementRepository
  ) {}

  update(callCenter: CallCenter): Observable<void> {
    return this.callCenterRepository.update(callCenter).pipe(
      delayWhen(() => this.callCenterContext.reload()),
      wrapError('call-center-context-service:update-call-center')
    );
  }

  updateQueue(callCenterId: number, queueId: number, queue: Queue): Observable<void> {
    return this.callCenterRepository.updateQueue(callCenterId, queueId, queue).pipe(
      delayWhen(() => this.queueContext.reload()),
      wrapError('call-center-service:update-queue')
    );
  }
}
